/**
 * WSAdminGigapodPage — GIGAPOD 바로가기 연동 설정
 *
 * 체크박스 ON + 유효한 http(s) URL 일 때만 좌측 모듈 레일에 GIGAPOD 링크 버튼이 노출된다.
 * 설정은 workspace_settings.gigapod (Supabase) 에 저장되고 전 멤버가 읽는다.
 *
 * NOTE: "자동 로그인 URL 자동 생성" 은 후속 단계 계획 — 이번 범위는 URL 직접 입력.
 */
import React, { useEffect, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Save } from 'lucide-react';
import { useMyRole } from '../../../context/WorkspacePermissionContext';
import { useWorkspaceSettings, useRefreshWorkspaceSettings } from '../../../context/WorkspaceSettingsContext';
import { Workspace } from '../../../models/Workspace';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { toast } from '../../../components/ui/use-toast';
import {
  GigapodSettings, normalizeGigapodUrl, isValidGigapodUrl,
} from '../../../models/WorkspaceSettings';
import { updateWorkspaceSettings } from '../../../services/WorkspaceSettingsService';
import { GIGAPOD_ICON_SRC } from '../../../const/Gigapod';

interface Props {
  workspace: Workspace;
}

const WSAdminGigapodPage: React.FC<Props> = ({ workspace }) => {
  const myRole = useMyRole();
  const canManage = myRole === 'owner' || myRole === 'admin';

  const settings = useWorkspaceSettings();
  const refresh = useRefreshWorkspaceSettings();
  const [draft, setDraft] = useState<GigapodSettings>(settings.gigapod);
  const [saving, setSaving] = useState(false);

  // 외부 설정 변경(워크스페이스 전환·refresh) 시 draft sync
  useEffect(() => { setDraft(settings.gigapod); }, [settings.gigapod]);

  if (!canManage) {
    return (
      <div className="px-6 py-8">
        <p className="text-muted-foreground"><Trans>Only workspace administrators can access this page.</Trans></p>
      </div>
    );
  }

  const dirty =
    draft.enabled !== settings.gigapod.enabled ||
    draft.url !== settings.gigapod.url;

  // 체크 ON 인데 URL 이 http(s) 로 유효하지 않으면 인라인 경고 (빈 값 포함)
  const urlInvalid = draft.enabled && !isValidGigapodUrl(normalizeGigapodUrl(draft.url));

  const handleSave = async () => {
    if (!dirty || saving) return;
    const normalized = normalizeGigapodUrl(draft.url);
    // 링크를 노출하는 설정일 때만 URL 유효성을 강제 (OFF 면 입력값 보존)
    if (draft.enabled && !isValidGigapodUrl(normalized)) {
      toast({ title: t`Enter a valid URL starting with http:// or https://`, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const next: GigapodSettings = { enabled: draft.enabled, url: normalized };
      await updateWorkspaceSettings(workspace.id, { gigapod: next });
      await refresh();
      setDraft(next);  // 스킴 보정된 값(https:// 자동 부착)을 입력창에 반영
      toast({ title: t`GIGAPOD settings saved`, duration: 2000 });
    } catch (err) {
      console.error('[Gigapod] 설정 저장 실패:', err);
      toast({ title: t`Failed to save`, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full p-4 md:p-6 lg:p-8 space-y-8">
      {/* 페이지 헤더 */}
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl bg-muted border border-border">
          <img src={GIGAPOD_ICON_SRC} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
        </div>
        <div>
          <h2 className="text-2xl font-bold tracking-tight"><Trans>GIGAPOD Settings</Trans></h2>
          <p className="text-sm text-muted-foreground"><Trans>Manage preferences for the GIGAPOD integration.</Trans></p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle><Trans>General Information</Trans></CardTitle>
          <CardDescription><Trans>Visible to all members of the workspace.</Trans></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 링크 버튼 노출 여부 */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="gigapod-enabled"
              checked={draft.enabled}
              onCheckedChange={checked => setDraft(d => ({ ...d, enabled: checked === true }))}
              className="mt-0.5"
            />
            <Label htmlFor="gigapod-enabled" className="cursor-pointer leading-relaxed">
              <Trans>Show the GIGAPOD link button in the left menu.</Trans>
            </Label>
          </div>

          {/* 이동할 URL */}
          <div className="space-y-2">
            <Label htmlFor="gigapod-url">
              <Trans>Enter the GIGAPOD URL to open when the link button is clicked.</Trans>
            </Label>
            <Input
              id="gigapod-url"
              type="url"
              inputMode="url"
              autoComplete="off"
              value={draft.url}
              onChange={e => setDraft(d => ({ ...d, url: e.target.value }))}
              placeholder="https://my-gigapod.jp"
              className="bg-muted border-border"
              aria-invalid={urlInvalid || undefined}
            />
            {urlInvalid && (
              <p className="text-meta-sm text-destructive">
                <Trans>Enter a valid URL starting with http:// or https://</Trans>
              </p>
            )}
          </div>
        </CardContent>
        <CardFooter className="border-t px-6 py-4 bg-muted/50 flex justify-end">
          <Button onClick={handleSave} disabled={saving || !dirty}>
            {saving
              ? <Trans>Saving...</Trans>
              : <><Save className="w-4 h-4 mr-2" /><Trans>Save Changes</Trans></>}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default WSAdminGigapodPage;
